import React from 'react';
import { formatGap, gapDirection, isNum } from '../format';

function niceBounds(values) {
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values);
  const pad = Math.max(1.5, (hi - lo) * 0.12);
  return [Math.floor((lo - pad) / 2) * 2, Math.ceil((hi + pad) / 2) * 2];
}

/**
 * Where this entity sits against the middle half of Sydney suburbs.
 * Plain positioned HTML rather than SVG so the text stays crisp and sized in
 * rem at every width, and it renders identically on server and client.
 */
export default function GapScale({ value, distribution, label, referenceLabel = 'Middle half of Sydney suburbs' }) {
  if (!distribution || !isNum(value)) return null;
  const { guide_gap_p25: p25, guide_gap_p50: p50, guide_gap_p75: p75 } = distribution;
  const [lo, hi] = niceBounds([p25, p50, p75, value]);
  const at = (v) => `${(((v - lo) / (hi - lo)) * 100).toFixed(2)}%`;
  const direction = gapDirection(value);
  const markerLeft = ((value - lo) / (hi - lo)) * 100;
  const pos = (v) => ((v - lo) / (hi - lo)) * 100;
  // "At guide" sits above the rail unless the marker label is there, then
  // below it unless a tick is there; otherwise the zero line goes unlabelled.
  const zeroAbove = Math.abs(pos(0) - pos(value)) > 28;
  const zeroBelow = Math.abs(pos(0) - pos(p25)) > 16 && Math.abs(pos(0) - pos(p75)) > 16;
  const align = markerLeft > 70 ? 'end' : markerLeft < 30 ? 'start' : 'middle';

  return (
    <figure className="re-scale" aria-label={`${label} ${formatGap(value)}. ${referenceLabel}: ${formatGap(p25)} to ${formatGap(p75)}, middle ${formatGap(p50)}.`}>
      <div className="re-scale__track" aria-hidden="true">
        <span className={`re-scale__marker-label re-scale__marker-label--${align}`} style={{ left: at(value) }}>
          <strong>{label}</strong> {formatGap(value)}
        </span>
        <span className="re-scale__rail" />
        <span className="re-scale__band" style={{ left: at(p25), width: `calc(${at(p75)} - ${at(p25)})` }} />
        <span className="re-scale__median" style={{ left: at(p50) }} />
        <span className="re-scale__zero" style={{ left: at(0) }} />
        <span className={`re-scale__marker re-scale__marker--${direction}`} style={{ left: at(value) }} />
        <span className="re-scale__tick" style={{ left: at(p25) }}>{formatGap(p25)}</span>
        <span className="re-scale__tick" style={{ left: at(p75) }}>{formatGap(p75)}</span>
        {zeroAbove || zeroBelow ? (
          <span className={`re-scale__zero-label re-scale__zero-label--${zeroAbove ? 'above' : 'below'}`} style={{ left: at(0) }}>
            At guide
          </span>
        ) : null}
      </div>
      <figcaption>
        <span className="re-scale__key" aria-hidden="true" /> {referenceLabel}, median {formatGap(p50)}
      </figcaption>
    </figure>
  );
}
